/**
 * Coerce any incoming report-shaped value into a *guaranteed-safe* Report
 * before the dashboard renders. Missing/malformed fields are filled with
 * empty defaults so component-level `.map()` and `.slice()` calls can never
 * blow up on undefined arrays or absent nested objects.
 *
 * Rationale: the backend has a validator (reportGenerator.ts) but older
 * reports in the DB pre-date it. Rather than scattering null-checks across
 * every render site, we sanitize once at the boundary.
 *
 * Render-time guards in Dashboard.tsx are still useful as defense-in-depth
 * (e.g. early-return when capboi is empty), but no component should be able
 * to crash because a sibling field is undefined.
 */

import type {
  Report,
  ReportBook,
  ReportCapboiTrait,
  ReportCareer,
  ReportCareerGrowth,
  ReportPersonality,
  ReportRadarValues,
  ReportStrategy,
  ReportStrategyPhase,
  ReportStrategyTask,
  ReportTraitCard,
} from './reportTypes';

const EMPTY_RADAR: ReportRadarValues = {
  communal: 0,
  analytical: 0,
  pragmatic: 0,
  bold: 0,
  orthodox: 0,
  imaginative: 0,
};

const EMPTY_PEER_RADAR: ReportRadarValues = {
  communal: 50,
  analytical: 50,
  pragmatic: 50,
  bold: 50,
  orthodox: 50,
  imaginative: 50,
};

const EMPTY_STRATEGY: ReportStrategy = { intro: '', phases: [] };

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function strArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

function normalizeRadar(v: unknown): ReportRadarValues {
  if (v == null || typeof v !== 'object') return { ...EMPTY_RADAR };
  const r = v as Record<string, unknown>;
  return {
    communal: num(r.communal),
    analytical: num(r.analytical),
    pragmatic: num(r.pragmatic),
    bold: num(r.bold),
    orthodox: num(r.orthodox),
    imaginative: num(r.imaginative),
  };
}

function normalizeCapboiTrait(v: unknown): ReportCapboiTrait | null {
  if (v == null || typeof v !== 'object') return null;
  const t = v as Record<string, unknown>;
  const key = t.key;
  // The dashboard's color lookup expects key in this set — anything else,
  // drop the trait entirely rather than render with an undefined color.
  if (key !== 'I' && key !== 'C' && key !== 'B' && key !== 'A' && key !== 'P' && key !== 'O') {
    return null;
  }
  return {
    key,
    name: str(t.name),
    score: num(t.score),
    one: str(t.one),
    long: str(t.long),
    high: strArray(t.high),
    low: strArray(t.low),
  };
}

function normalizeCapboi(v: unknown): ReportCapboiTrait[] {
  if (!Array.isArray(v)) return [];
  return v.map(normalizeCapboiTrait).filter((t): t is ReportCapboiTrait => t !== null);
}

function normalizePersonality(v: unknown): ReportPersonality {
  if (v == null || typeof v !== 'object') {
    return {
      code: '',
      typeName: '',
      typeTagline: '',
      description: '',
      dominantAxes: ['', ''],
      dominantAxesDescription: '',
      radar: { ...EMPTY_RADAR },
      peerRadar: { ...EMPTY_PEER_RADAR },
      capboi: [],
    };
  }
  const p = v as Record<string, unknown>;
  const axes = Array.isArray(p.dominantAxes) ? p.dominantAxes : [];
  return {
    code: str(p.code),
    typeName: str(p.typeName),
    typeTagline: str(p.typeTagline),
    description: str(p.description),
    dominantAxes: [str(axes[0]), str(axes[1])] as [string, string],
    dominantAxesDescription: str(p.dominantAxesDescription),
    radar: normalizeRadar(p.radar),
    peerRadar: normalizeRadar(p.peerRadar),
    capboi: normalizeCapboi(p.capboi),
  };
}

function normalizeTraitCard(v: unknown): ReportTraitCard | null {
  if (v == null || typeof v !== 'object') return null;
  const t = v as Record<string, unknown>;
  if (typeof t.name !== 'string' || typeof t.description !== 'string') return null;
  return { name: t.name, description: t.description };
}

function normalizeTraitCardArray(v: unknown): ReportTraitCard[] {
  if (!Array.isArray(v)) return [];
  return v.map(normalizeTraitCard).filter((t): t is ReportTraitCard => t !== null);
}

function normalizeGrowth(v: unknown): ReportCareerGrowth {
  if (v == null || typeof v !== 'object') {
    return { rate: '—', label: 'Steady', trend: 'flat' };
  }
  const g = v as Record<string, unknown>;
  const trend =
    g.trend === 'up' || g.trend === 'down' || g.trend === 'flat' ? g.trend : 'flat';
  return {
    rate: str(g.rate, '—'),
    label: str(g.label, 'Steady'),
    trend,
  };
}

function normalizeCareer(v: unknown): ReportCareer | null {
  if (v == null || typeof v !== 'object') return null;
  const c = v as Record<string, unknown>;
  // Must have at least a title; otherwise we can't render a meaningful card.
  if (typeof c.title !== 'string' || c.title.trim().length === 0) return null;
  return {
    id: str(c.id, c.title.toLowerCase().replace(/\s+/g, '-')),
    title: c.title,
    matchPercent: Math.max(0, Math.min(100, num(c.matchPercent))),
    field: str(c.field),
    salaryEntry: num(c.salaryEntry),
    salaryMid: num(c.salaryMid),
    salarySenior: num(c.salarySenior),
    growth: normalizeGrowth(c.growth),
    description: str(c.description),
    fitReasons: strArray(c.fitReasons),
    haveSkills: strArray(c.haveSkills),
    needSkills: strArray(c.needSkills),
    daily: str(c.daily),
  };
}

function normalizeCareers(v: unknown): ReportCareer[] {
  if (!Array.isArray(v)) return [];
  return v.map(normalizeCareer).filter((c): c is ReportCareer => c !== null);
}

function normalizeBook(v: unknown): ReportBook | null {
  if (v == null || typeof v !== 'object') return null;
  const b = v as Record<string, unknown>;
  if (typeof b.title !== 'string' || b.title.trim().length === 0) return null;
  return {
    title: b.title,
    subtitle: str(b.subtitle),
    author: str(b.author),
    why: str(b.why),
    amazonUrl: typeof b.amazonUrl === 'string' ? b.amazonUrl : undefined,
  };
}

function normalizeBooks(v: unknown): ReportBook[] {
  if (!Array.isArray(v)) return [];
  return v.map(normalizeBook).filter((b): b is ReportBook => b !== null);
}

function normalizeTask(v: unknown): ReportStrategyTask {
  if (typeof v === 'string') return { t: v, done: false };
  if (v == null || typeof v !== 'object') return { t: '', done: false };
  const o = v as Record<string, unknown>;
  return {
    t: typeof o.t === 'string' ? o.t : '',
    done: o.done === true,
  };
}

function normalizePhase(v: unknown): ReportStrategyPhase | null {
  if (v == null || typeof v !== 'object') return null;
  const p = v as Record<string, unknown>;
  return {
    label: str(p.label),
    title: str(p.title),
    goal: str(p.goal),
    tasks: Array.isArray(p.tasks) ? p.tasks.map(normalizeTask) : [],
  };
}

function normalizeStrategy(v: unknown): ReportStrategy {
  if (v == null || typeof v !== 'object') return { ...EMPTY_STRATEGY };
  const s = v as Record<string, unknown>;
  const phases = Array.isArray(s.phases)
    ? s.phases.map(normalizePhase).filter((p): p is ReportStrategyPhase => p !== null)
    : [];
  return {
    intro: str(s.intro),
    phases,
  };
}

/**
 * Main entry — coerce any value into a Report with every field guaranteed
 * to be the correct shape. Pass *any* untrusted input here; downstream
 * components can then treat the result as fully-typed without null checks.
 */
export function normalizeReport(input: unknown): Report {
  const r = (input ?? {}) as Record<string, unknown>;
  return {
    bio: str(r.bio),
    educationLine: str(r.educationLine),
    currentTitleLine: str(r.currentTitleLine),
    personality: normalizePersonality(r.personality),
    strengths: normalizeTraitCardArray(r.strengths),
    strengthsSummary: str(r.strengthsSummary),
    weaknesses: normalizeTraitCardArray(r.weaknesses),
    weaknessesSummary: str(r.weaknessesSummary),
    workplaceTraits: normalizeTraitCardArray(r.workplaceTraits),
    workplaceTraitsSummary: str(r.workplaceTraitsSummary),
    careers: normalizeCareers(r.careers),
    books: normalizeBooks(r.books),
    takeaways: strArray(r.takeaways),
    strategy: normalizeStrategy(r.strategy),
  };
}
